import type { Plugin } from "../../core/plugin";
import { PluginRegistry } from "../../core/pluginRegistry";

/**
 * Dropdown sencillo y autónomo para los formularios de TravelPricer.
 * Implementa el patrón ARIA "select-only combobox": el foco permanece en el
 * trigger y la opción activa se comunica mediante aria-activedescendant.
 */
export class FormSelect implements Plugin {
  private readonly trigger: HTMLElement | null;
  private readonly valueEl: HTMLElement | null;
  private readonly input: HTMLInputElement | null;
  private readonly dropdown: HTMLElement | null;
  private readonly options: HTMLElement[];

  private activeIndex = -1;
  private typeahead = "";
  private typeaheadTimer: number | undefined;

  constructor(private readonly element: HTMLElement) {
    this.trigger = element.querySelector("[data-form-select-trigger]");
    this.valueEl = element.querySelector("[data-form-select-value]");
    this.input = element.querySelector("[data-form-select-input]");
    this.dropdown = element.querySelector("[data-form-select-dropdown]");
    this.options = Array.from(
      element.querySelectorAll<HTMLElement>("[data-form-select-option]"),
    );
  }

  init(): void {
    this.trigger?.addEventListener("click", this.handleTriggerClick);
    this.trigger?.addEventListener("keydown", this.handleKeydown);
    this.options.forEach((option) => {
      option.addEventListener("click", this.handleOptionClick);
      option.addEventListener("mousemove", this.handleOptionHover);
    });
    this.close();
  }

  destroy(): void {
    this.trigger?.removeEventListener("click", this.handleTriggerClick);
    this.trigger?.removeEventListener("keydown", this.handleKeydown);
    this.options.forEach((option) => {
      option.removeEventListener("click", this.handleOptionClick);
      option.removeEventListener("mousemove", this.handleOptionHover);
    });
    document.removeEventListener("click", this.handleDocumentClick);
  }

  private isOpen(): boolean {
    return this.element.classList.contains("is-open");
  }

  private readonly handleTriggerClick = (): void => {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  };

  private readonly handleOptionClick = (event: Event): void => {
    const option = event.currentTarget as HTMLElement;
    this.select(this.options.indexOf(option));
    this.close();
    this.trigger?.focus();
  };

  private readonly handleOptionHover = (event: Event): void => {
    this.setActive(this.options.indexOf(event.currentTarget as HTMLElement));
  };

  private readonly handleDocumentClick = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) {
      this.close();
    }
  };

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (this.isOpen()) {
          this.setActive(this.activeIndex + 1);
        } else {
          this.open();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (this.isOpen()) {
          this.setActive(this.activeIndex - 1);
        } else {
          this.open();
        }
        break;
      case "Home":
        if (this.isOpen()) {
          event.preventDefault();
          this.setActive(0);
        }
        break;
      case "End":
        if (this.isOpen()) {
          event.preventDefault();
          this.setActive(this.options.length - 1);
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (this.isOpen()) {
          this.select(this.activeIndex);
          this.close();
        } else {
          this.open();
        }
        break;
      case "Escape":
        if (this.isOpen()) {
          event.preventDefault();
          this.close();
        }
        break;
      case "Tab":
        if (this.isOpen()) {
          this.close();
        }
        break;
      default:
        if (event.key.length === 1 && /\S/.test(event.key)) {
          this.handleTypeahead(event.key);
        }
        break;
    }
  };

  private open(): void {
    FormSelect.closeOthers(this.element);
    this.element.classList.add("is-open");
    this.dropdown?.removeAttribute("hidden");
    this.trigger?.setAttribute("aria-expanded", "true");
    document.addEventListener("click", this.handleDocumentClick);

    const selectedIndex = this.options.findIndex(
      (option) => option.getAttribute("aria-selected") === "true",
    );
    this.setActive(selectedIndex >= 0 ? selectedIndex : 0);
  }

  private close(): void {
    this.element.classList.remove("is-open");
    this.dropdown?.setAttribute("hidden", "");
    this.trigger?.setAttribute("aria-expanded", "false");
    this.trigger?.removeAttribute("aria-activedescendant");
    this.clearActive();
    document.removeEventListener("click", this.handleDocumentClick);
  }

  private setActive(index: number): void {
    if (this.options.length === 0) return;

    const clamped = Math.max(0, Math.min(index, this.options.length - 1));
    this.clearActive();

    this.activeIndex = clamped;
    const option = this.options[clamped];
    option.classList.add("is-active");
    if (option.id) {
      this.trigger?.setAttribute("aria-activedescendant", option.id);
    }
    option.scrollIntoView({ block: "nearest" });
  }

  private clearActive(): void {
    this.options.forEach((option) => option.classList.remove("is-active"));
    this.activeIndex = -1;
  }

  private select(index: number): void {
    const option = this.options[index];
    if (!option || !this.input) return;

    const value = option.dataset.value ?? "";
    const label = option.textContent?.trim() ?? "";
    const hasChanged = this.input.value !== value;

    if (this.valueEl) this.valueEl.textContent = label;
    this.input.value = value;

    this.options.forEach((item) =>
      item.setAttribute("aria-selected", item === option ? "true" : "false"),
    );

    // Asignar `.value` por JS no dispara eventos nativos: los emitimos a mano
    // para que el input oculto se comporte como un <select> real de cara a
    // cualquier listener de formulario externo (validación, submit, etc.).
    if (hasChanged) {
      this.input.dispatchEvent(new Event("input", { bubbles: true }));
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  private handleTypeahead(char: string): void {
    window.clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = window.setTimeout(() => {
      this.typeahead = "";
    }, 500);

    const match = this.options.findIndex((option) =>
      (option.textContent ?? "")
        .trim()
        .toLowerCase()
        .startsWith(this.typeahead),
    );
    if (match < 0) return;

    if (this.isOpen()) {
      this.setActive(match);
    } else {
      this.select(match);
    }
  }

  private static closeOthers(current: HTMLElement): void {
    document
      .querySelectorAll<HTMLElement>(".trv-formSelect.is-open")
      .forEach((el) => {
        if (el === current) return;
        el.classList.remove("is-open");
        el.querySelector("[data-form-select-dropdown]")?.setAttribute(
          "hidden",
          "",
        );
        const trigger = el.querySelector("[data-form-select-trigger]");
        trigger?.setAttribute("aria-expanded", "false");
        trigger?.removeAttribute("aria-activedescendant");
        el.querySelectorAll(".trv-formSelect__option.is-active").forEach(
          (option) => option.classList.remove("is-active"),
        );
      });
  }
}

PluginRegistry.register("trv-form-select", FormSelect);
